import './EventRegisterForm.scss';

import { Cancel, NavigateNext } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  CircularProgress,
  Collapse,
  Grid,
  Switch,
  TextField,
} from '@mui/material';
import { IEventRepository } from 'domain/event/Event.repository';
import { EventParticipant } from 'domain/event/participants/EventParticipant';
import { PlayersNumberByTypeTeams, TypeTeam } from 'domain/event/TypeTeam';
import { TypeTeamCodeEnum } from 'domain/event/TypeTeamCodeEnum';
import { UnpyEvent } from 'domain/event/UnpyEvent';
import { UnpyEventView } from 'domain/event/UnpyEventView';
import { CreateTeamPlayerFields } from 'primary/Components/Event/CreateTeamPlayerFields';
import { EventRegisterFormData } from 'primary/Components/Event/RegisterEventFormStep/FormStepPlayers';
import { InputWrapper } from 'primary/Components/Input/InputWrapper';
import { StructureSelector } from 'primary/Components/Ping/StructureSelector';
import { sendEventToastMessage } from 'primary/Components/Toast/Toast.helper';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useTranslate, UseTranslateReturn } from 'primary/hooks/useTranslate';
import { setTeamCreating } from 'primary/teams/store/TeamCreatingSlice';
import React, { useState } from 'react';
import { useController, UseFormReturn } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RegisterTypeEnum } from 'secondary/event/RegisterTypeEnum';

type EventRegisterFormProps = {
  event: UnpyEvent | UnpyEventView;
  form: UseFormReturn<EventRegisterFormData>;
  typeTeam: TypeTeam;
  onChangeField?: (name: string, property: string, value?: string) => void;
  closeModal?: () => void;
  onSubmitted?: (eventParticipant?: EventParticipant) => void;
  randomTeams?: boolean;
};
const useOnSubmit =
  (
    eventRepository: IEventRepository,
    eventId: number,
    onSubmitted: (eventPArticipant: EventParticipant) => void,
    setLoadingState: (loadingState: boolean) => void,
    t: UseTranslateReturn,
  ) =>
  (data: EventRegisterFormData) => {
    setLoadingState(true);
    eventRepository
      .createTeam(data, eventId)
      .then((data) => {
        onSubmitted(data);
        sendEventToastMessage(t('event.register.success'), 'success');
      })
      .finally(() => {
        setLoadingState(false);
      });
  };
export const EventRegisterForm = ({
  form,
  typeTeam,
  onChangeField,
  event,
  closeModal,
  onSubmitted,
  randomTeams,
}: EventRegisterFormProps) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    formState: { isSubmitting, errors },
  } = form;
  const { eventRepository, authRepository, codeEventRepository } = useContextDependency();
  const [isLoading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const t = useTranslate();

  const [errorCode, setErrorCode] = React.useState<boolean>(false);
  const [isCodeLoading, setIsCodeLoading] = React.useState<boolean>(false);
  const onSubmit = useOnSubmit(
    eventRepository,
    event.id,
    (participant) => {
      dispatch(setTeamCreating(undefined));
      closeModal?.();
      onSubmitted?.(participant);
    },
    setLoading,
    t,
  );

  const enhancedOnSubmit = async (data: EventRegisterFormData) => {
    if (event.registerType === RegisterTypeEnum.CODE) {
      setIsCodeLoading(true);
      setErrorCode(false);
      if (!data.code) return;
      const { isConsummable: isCodeValid } = await codeEventRepository.checkValidity(
        data.code,
        event.id,
      );
      if (!isCodeValid) {
        setErrorCode(true);
        setIsCodeLoading(false);
        return Promise.reject();
      } else {
        return onSubmit(data);
      }
    } else {
      return onSubmit(data);
    }
  };

  const structureControl = useController({
    control: control,
    name: 'structure',
  });

  const navigate = useNavigate();
  const handleCancel = () => {
    closeModal?.();
    dispatch(setTeamCreating(undefined));
  };

  const isParticipationAsStructure = watch('participationAsStructure');
  return (
    <form onSubmit={handleSubmit(enhancedOnSubmit)} className={'-eventRegisterForm'}>
      <Grid container spacing={2}>
        {!randomTeams && (
          <Grid item xs={12} sm={6}>
            <InputWrapper
              className={'eventAppearenceForm__field'}
              label={t('event.modal.nameLabel')}
              errors={errors}
              isSubmitting={isSubmitting}
              Input={TextField}
              inputProps={{
                placeholder: t('event.modal.namePlaceholder'),
                disabled: isSubmitting,
                ...register(`name`),
                variant: 'filled',
              }}
            />
          </Grid>
        )}
        {event.registerType === RegisterTypeEnum.CODE && (
          <Grid item xs={12} sm={6}>
            <InputWrapper
              className={'eventAppearenceForm__field'}
              label={t('event.modal.codeLabel')}
              errors={errors}
              isSubmitting={isSubmitting}
              Input={TextField}
              required
              inputProps={{
                placeholder: t('event.modal.codePlaceholder'),
                disabled: isSubmitting,
                ...register(`code`),
                variant: 'filled',
              }}
            />

            <div className={'-codeCheckContainer'}>
              {isCodeLoading && (
                <>
                  <CircularProgress size={'small'} color={'info'} />
                  <span>vérificaiton du code</span>
                </>
              )}
              {!isCodeLoading && errorCode && (
                <span className={'-errorCode'}>Code invalide</span>
              )}
            </div>
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <InputWrapper
            label={t('event.modal.selectStructureHelper')}
            errors={errors}
            className={'unpy-form__field'}
            isSubmitting={isSubmitting}
            Input={Switch}
            noFullWidth
            inputProps={{
              ...register('participationAsStructure', { required: true }),
              defaultChecked: !!getValues('participationAsStructure'),
            }}
          />
          <Collapse in={isParticipationAsStructure && !!authRepository.currentUser?.id}>
            <StructureSelector
              userId={authRepository?.currentUser?.id as number}
              errors={errors}
              control={structureControl}
            />
          </Collapse>
        </Grid>
      </Grid>
      <div className={'-addingPlayerContainer'}>
        <div className={'-playersField'}>
          {PlayersNumberByTypeTeams[
            randomTeams ? TypeTeamCodeEnum.SOL : typeTeam.code
          ]?.map((nb, index) => (
            <CreateTeamPlayerFields
              randomTeams={randomTeams}
              key={nb + '-' + index}
              form={form}
              index={index}
              structureId={watch('structure')}
              disabledPlayer={index === 0}
              disabledRole={index === 0}
              onChangeField={onChangeField}
              eventId={event.id}
              creatorIdPlayer={authRepository.currentUser?.userProfil?.idPlayer as string}
            />
          ))}
        </div>
      </div>
      <div className={'-buttons'}>
        <Button
          variant={'outlined'}
          color={'primary'}
          onClick={() => handleCancel()}
          className={'cancel'}
        >
          <Cancel />
          {t('event.register.cancelCreatingTeam')}
        </Button>
        <LoadingButton
          variant={'contained'}
          color={'primary'}
          type={'submit'}
          loading={isLoading}
        >
          {t('event.register.createTeam')}
          <NavigateNext />
        </LoadingButton>
      </div>
    </form>
  );
};
